import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useTenant } from './TenantContext';
import customTheme from '../theme/theme';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const { tenant } = useTenant();
  const [primaryColor, setPrimaryColor] = useState(() => {
    // Initialize with value from localStorage if available, otherwise use default
    const storedTenant = localStorage.getItem('currentTenant');
    const tenantId = localStorage.getItem('tenantId');
    
    if (storedTenant && tenantId) {
      try {
        const parsedTenant = JSON.parse(storedTenant);
        // Only use color if tenant IDs match to ensure proper isolation
        if (parsedTenant && parsedTenant.primaryColor && 
            (parsedTenant._id === tenantId || parsedTenant.id === tenantId)) {
          console.log(`ThemeContext: Using tenant ${tenantId}'s primary color: ${parsedTenant.primaryColor}`);
          return parsedTenant.primaryColor;
        }
      } catch (e) {
        console.error('Error parsing tenant from localStorage:', e);
      }
    }
    return customTheme.palette.primary.main; // Use our custom theme's primary color as default
  });
  
  // Store tenant ID for validation
  const [currentTenantId, setCurrentTenantId] = useState(
    localStorage.getItem('tenantId') || null
  );

  useEffect(() => {
    // Update tenant ID and primary color when tenant changes
    if (tenant) {
      const tenantId = tenant._id || tenant.id;
      
      // Only update if we have a valid tenant ID and it differs from current
      if (tenantId && tenantId !== currentTenantId) {
        setCurrentTenantId(tenantId);
        localStorage.setItem('tenantId', tenantId);
        
        // Log for transparency
        console.log(`ThemeContext: Tenant changed to ${tenantId}`);
      }
      
      // Only update color if tenant has one defined
      if (tenant.primaryColor) {
        console.log(`ThemeContext: Applying color ${tenant.primaryColor} for tenant ${tenantId}`);
        setPrimaryColor(tenant.primaryColor);
      }
    }
  }, [tenant, currentTenantId]);

  // Enhanced function to update primary color with tenant ID validation
  const updatePrimaryColor = useCallback((newColor) => {
    const tenantId = localStorage.getItem('tenantId');
    const storedTenant = localStorage.getItem('currentTenant');
    
    // Safety checks to ensure we're operating within the correct tenant
    if (!tenantId) {
      console.error('ThemeContext: Cannot update color - no tenant ID available');
      return false;
    }
    
    // Update in-memory state
    setPrimaryColor(newColor);
    
    // Secure update to localStorage with tenant ID validation
    if (storedTenant) {
      try {
        const parsedTenant = JSON.parse(storedTenant);
        
        // Verify tenant ID matches for security
        if (parsedTenant && (parsedTenant._id === tenantId || parsedTenant.id === tenantId)) {
          console.log(`ThemeContext: Updating primary color for tenant ${tenantId} to ${newColor}`);
          parsedTenant.primaryColor = newColor;
          localStorage.setItem('currentTenant', JSON.stringify(parsedTenant));
          return true;
        } else {
          console.error('ThemeContext: Tenant ID mismatch - color update rejected');
          return false;
        }
      } catch (e) {
        console.error('Error updating primaryColor in localStorage:', e);
        return false;
      }
    }
    
    return false;
  }, []);

  // Create a copy of the custom theme with the tenant's primary color
  const theme = {
    ...customTheme,
    palette: {
      ...customTheme.palette,
      primary: {
        ...customTheme.palette.primary,
        main: primaryColor || customTheme.palette.primary.main,
      },
    },
    components: {
      ...customTheme.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: primaryColor || customTheme.palette.primary.main,
          },
        },
      },
      MuiButton: {
        ...customTheme.components.MuiButton,
        styleOverrides: {
          ...customTheme.components.MuiButton.styleOverrides,
          containedPrimary: {
            background: `linear-gradient(90deg, ${primaryColor || customTheme.palette.primary.main} 0%, ${adjustColor(primaryColor || customTheme.palette.primary.main, 20)} 100%)`,
            '&:hover': {
              background: `linear-gradient(90deg, ${adjustColor(primaryColor || customTheme.palette.primary.main, -20)} 0%, ${primaryColor || customTheme.palette.primary.main} 100%)`,
            },
          },
        },
      },
    },
  };

  // Create CSS variables for non-MUI components with tenant ID scope
  useEffect(() => {
    // Apply tenant-specific CSS variables if there's a valid tenant ID
    if (currentTenantId) {
      document.documentElement.style.setProperty('--primary-color', primaryColor || customTheme.palette.primary.main);
      document.documentElement.style.setProperty('--primary-color-light', adjustColor(primaryColor || customTheme.palette.primary.main, 30));
      document.documentElement.style.setProperty('--primary-color-dark', adjustColor(primaryColor || customTheme.palette.primary.main, -30));
      
      // Additional tenant-specific CSS variable with tenant ID for isolation
      document.documentElement.style.setProperty(`--tenant-${currentTenantId}-primary-color`, primaryColor || customTheme.palette.primary.main);
      
      console.log(`ThemeContext: Applied CSS variables for tenant ${currentTenantId} with color ${primaryColor}`);
    }
  }, [primaryColor, currentTenantId]);

  return (
    <ThemeContext.Provider value={{ 
      primaryColor, 
      setPrimaryColor: updatePrimaryColor,
      currentTenantId,
      // Additional helper function for tenant-specific styling
      getTenantStyle: (stylePropertyName, defaultValue) => {
        if (!currentTenantId) return defaultValue;
        return { [stylePropertyName]: primaryColor || defaultValue };
      }
    }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Helper function to adjust color lightness/darkness
function adjustColor(color, amount) {
  // Function to convert hex to RGB
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Function to convert RGB to hex
  const rgbToHex = (r, g, b) => {
    const componentToHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
  };

  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  return rgbToHex(
    rgb.r + amount,
    rgb.g + amount,
    rgb.b + amount
  );
}

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;